import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment/moment';
import {
  getEmailMarketingToggleError,
  getEmailMarketingTrialDate,
  getIsEmailMarketingTurnOffModalOpen
} from 'store/discountsV2/selectors';
import {useHistory, useLocation} from 'react-router-dom';
import {
  useDaysPickerContext,
  useDaysPickerContextUpdate
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import {
  CLEAR_DISCOUNT,
  OPEN_EMAIL_MARKETING_TURN_OFF_MODAL,
  SET_DISCOUNT_MODAL,
  SET_EMAIL_MARKETING_TOGGLE_ERROR
} from 'store/discountsV2/action';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import {
  DashboardField,
  DashboardFieldDatepicker,
  DashboardFieldInner,
  DashboardFieldLabel,
  DashboardFields,
  DashboardInnerDescription,
  DialogBody,
  FieldInput,
  FieldInputDollar,
  FieldLabel
} from 'routes/App/dashboard/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from 'components/DialogTitle/styles';
import DialogSubTitle from 'components/DialogSubTitle/styles';
import {Box, Checkbox, FormControlLabel} from '@material-ui/core';
import {
  DISCOUNT_POSTHOG_EVENTS,
  DISCOUNT_STATUS,
  DISCOUNT_TYPE
} from 'helpers/constants/discountsV2';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {ButtonInfo, ButtonPrimary} from 'components/buttons';
import {twoDecimalsFormatter} from 'helpers/functionUtils/helpFunctions';
import {
  DiscountsModalButton,
  DiscountsModalSwitch
} from 'routes/App/discountsV2/styles';
import SwitchS2E from 'components/Switch';

import isEmailMarketingTrialExpired from 'helpers/functionUtils/emailMarketing';
import EmailMarketingTurnOffModal from 'routes/App/discountsV2/DiscountsList/Modals/EmailMarketingTurnOffModal';
import DatepickerS2E from 'components/Datepicker';
import DaysPickerContainer from 'routes/App/discountsV2/DiscountWizard/DateAvailability/DaysPickerContainer';
import {
  convertScheduleFromServiceFormat,
  convertScheduleToServiceFormat
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/helpers';

const SettingsModal = (props) => {
  const {
    formik,
    discount,
    enabledDiscount,
    setEnabledDiscount,
    enabledDate,
    setEnabledDate,
    buttonStatus,
    allowedServiceFeatures,
    enabledCustomAvailability,
    setEnabledCustomAvailability,
    enabledAdvancedSettings,
    setEnabledAdvancedSettings,
    isOpen,
    onCloseAction
  } = props;

  const dispatch = useDispatch();
  const {activeLocation, userData} = useSelector((state) => state.user);
  const isEmailMarketingToggleError = getEmailMarketingToggleError();
  const isEmailMarketingTurnOffModalOpen = getIsEmailMarketingTurnOffModalOpen();
  const location = useLocation();
  const history = useHistory();
  const emailMarketingTrialDate = getEmailMarketingTrialDate();
  const {Timezone} = useSelector((state) => state.user.activeLocation);
  const visualChecked = !formik.values.isPromotional;

  const daysPickerContext = useDaysPickerContext();
  const setDaysPickerContext = useDaysPickerContextUpdate();

  useEffect(() => {
    if (isEmailMarketingToggleError) {
      formik.setFieldError(
        'marketing.email.isEnabled',
        'Another discount was already selected for Email Marketing, you can use only one'
      );
    }
    return () => dispatch(SET_EMAIL_MARKETING_TOGGLE_ERROR(false));
  }, [isEmailMarketingToggleError]);

  useEffect(() => {
    return () => {
      dispatch(CLEAR_DISCOUNT());
    };
  }, []);

  useEffect(() => {
    if (isOpen && discount && window?.posthog) {
      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.MODAL_OPENED, {
        mode: discount?.id ? 'edit' : 'new',
        discount_type:
          discount?.rate?.type === DISCOUNT_TYPE.PERCENTAGE
            ? 'percentage_off'
            : 'fixed_amount',
        location_id: activeLocation?._id,
        location_name: activeLocation?.BusinessName || activeLocation?.FullName,
        user_id: userData?._id,
        user_email: userData?.email,
        timestamp: moment().utc().format()
      });
    }
  }, [isOpen, discount]);

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseAction}
      disableEnforceFocus
      disableRestoreFocus
      scroll='body'
    >
      <DialogContainer className='DialogLgDiscounts'>
        <DialogClose onClick={onCloseAction} />
        <Box>
          <DialogTitle className='marginBottomLg'>
            {discount?.id ? 'Edit ' : 'Create New '}Discount -{' '}
            {formik.values?.rate?.type === DISCOUNT_TYPE.PERCENTAGE
              ? 'Percentage Off'
              : 'Fixed Amount'}
          </DialogTitle>
        </Box>
        <DialogBody>
          <DashboardFields>
            <DashboardField className='dFlex discountV2Name'>
              <DashboardFieldInner className='w60FLex DiscountsV2 FieldRelative'>
                <FieldLabel
                  className={cn('FieldLabelLg mBottomMd', {
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                >
                  Discount Name <span className='redText'>*</span>
                </FieldLabel>
                <FieldInput
                  className={cn('', {
                    error: formik.errors?.name && formik.touched?.name,
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                  name='name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder='Discount Name'
                />
                <ErrorSignWrapper className='ErrorSignWrapper top3'>
                  {formik.errors?.name && formik.touched?.name ? (
                    <ErrorSign>{formik.errors.name}</ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardFieldInner>
            </DashboardField>
            <DashboardField className='dFlex'>
              <DashboardFields className='marginMinus13'>
                <DashboardField className='wAuto w50mob'>
                  <FieldLabel
                    className={cn('FieldLabelLg mBottomMd', {
                      isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                    })}
                  >
                    Discount Value <span className='redText'>*</span>
                  </FieldLabel>
                  <FieldInput
                    className={cn('FieldNumberLg mBottomMd', {
                      error:
                        formik.errors?.rate?.value &&
                        formik.touched?.rate?.value,
                      isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                    })}
                    name='rate.value'
                    type='text'
                    onChange={(e) =>
                      formik.setFieldValue(
                        'rate.value',
                        twoDecimalsFormatter(
                          e.target.value,
                          formik.values?.rate?.value,
                          formik.values?.rate?.type === DISCOUNT_TYPE.PERCENTAGE
                            ? 90
                            : 9999.99
                        ) || ''
                      )
                    }
                    onBlur={(e) => {
                      const lastElement = e.target.value.slice(-1)[0];
                      if (lastElement === '.') {
                        formik.setFieldValue(
                          'rate.value',
                          e.target.value.slice(0, -1)
                        );
                      }
                    }}
                    value={formik.values?.rate?.value}
                    placeholder=''
                  />
                  <ErrorSignWrapper topPosition='-9%'>
                    {formik.errors?.rate?.value &&
                    formik.touched?.rate?.value ? (
                      <ErrorSign>{formik.errors?.rate?.value}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </DashboardField>
                <DashboardField className='wAuto w50mob'>
                  <FieldLabel
                    className={cn('FieldLabelLg mBottomMd', {
                      isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                    })}
                  >
                    Min Order{' '}
                    {formik?.values?.rate?.type === DISCOUNT_TYPE.FIXED && (
                      <span className='redText'>*</span>
                    )}
                  </FieldLabel>
                  <FieldInputDollar>
                    <FieldInput
                      className={cn('FieldNumberLg', {
                        error: formik.errors?.subtotal?.min,
                        isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                      })}
                      name='subtotal.min'
                      type='text'
                      onChange={(e) =>
                        formik.setFieldValue(
                          'subtotal.min',
                          twoDecimalsFormatter(
                            e.target.value,
                            formik.values?.subtotal?.min,
                            9999.99,
                            true
                          )
                        )
                      }
                      onBlur={(e) => {
                        const lastElement = e.target.value.slice(-1)[0];
                        if (lastElement === '.') {
                          formik.setFieldValue(
                            'subtotal.min',
                            e.target.value.slice(0, -1)
                          );
                        }
                      }}
                      value={formik.values?.subtotal?.min}
                      placeholder=''
                    />
                  </FieldInputDollar>
                  <ErrorSignWrapper topPosition='-9%'>
                    {formik.errors?.subtotal?.min ? (
                      <ErrorSign>{formik.errors.subtotal?.min}</ErrorSign>
                    ) : null}
                  </ErrorSignWrapper>
                </DashboardField>
              </DashboardFields>
            </DashboardField>
          </DashboardFields>
          <DiscountsModalSwitch className='mTop17'>
            <div className='LeftBlock'>
              <SwitchS2E
                labelPlacement='start'
                checked={enabledDiscount}
                onChange={(e) => {
                  if (!e.target.checked) {
                    formik.setFieldValue('promoCode', null);
                    formik.setFieldValue('isPromotional', true);
                  } else {
                    formik.setFieldValue('promoCode', {
                      code: '',
                      usageLimit: null
                    });
                  }
                  setEnabledDiscount(e.target.checked);
                }}
                name='checkedActive'
                isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
              />
              <span className='switcher-text'>
                {enabledDiscount ? 'Active' : 'Disabled'}
              </span>
            </div>
            <div className='RightBlock'>
              <div className='title'>Promo code</div>
              <div className='description'>
                Require customer to enter a code
              </div>
              {enabledDiscount && (
                <>
                  <DashboardFieldInner className='w100Auto DiscountsV2 FieldRelative'>
                    <FieldLabel
                      className={cn('FieldLabelLg mBottomMd inline-label', {
                        isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                      })}
                    >
                      Enter promo code <span className='redText'>*</span>
                    </FieldLabel>
                    <FieldInput
                      className={cn('', {
                        error: formik.errors?.promoCode?.code,
                        isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                      })}
                      name='promoCode.code'
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={
                        formik.values?.promoCode?.code
                          ? formik.values.promoCode.code.toUpperCase()
                          : null
                      }
                      placeholder=''
                    />
                    <ErrorSignWrapper className='ErrorSignWrapper top3'>
                      {formik.errors?.promoCode?.code &&
                      formik.touched?.promoCode?.code ? (
                        <ErrorSign>{formik.errors.promoCode.code}</ErrorSign>
                      ) : null}
                    </ErrorSignWrapper>
                  </DashboardFieldInner>
                </>
              )}
            </div>
          </DiscountsModalSwitch>
          {enabledDiscount &&
            allowedServiceFeatures.includes('one_time_coupon') && (
              <DiscountsModalSwitch>
                <div className='LeftBlock'>
                  <SwitchS2E
                    labelPlacement='start'
                    checked={
                      formik.values?.promoCode?.usageLimit?.type ===
                        'PerCustomer' &&
                      formik.values?.promoCode?.usageLimit?.maxUsages === 1
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue(
                          'promoCode.usageLimit.type',
                          'PerCustomer'
                        );
                        formik.setFieldValue(
                          'promoCode.usageLimit.maxUsages',
                          1
                        );
                        formik.setFieldValue(
                          'promoCode.usageLimit.activatesAtLocal',
                          discount?.promoCode?.usageLimit?.activatesAtLocal ||
                            moment()
                              .utcOffset(
                                Number(activeLocation?.Timezone) || 0,
                                false
                              )
                              .utc(true)
                              .format()
                        );
                      } else {
                        formik.setFieldValue('promoCode.usageLimit', null);
                      }
                    }}
                    name='OneTimeMode.IsEnabled'
                    isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                  />
                  <span className='switcher-text'>
                    {formik.values?.promoCode?.usageLimit?.type ===
                      'PerCustomer' &&
                    formik.values?.promoCode?.usageLimit?.maxUsages === 1
                      ? 'Active'
                      : 'Disabled'}
                  </span>
                </div>
                <div className='RightBlock'>
                  <div className='title'>Single use per customer</div>
                  <div className='description'>
                    Limits the promo code to one use per customer
                  </div>
                </div>
              </DiscountsModalSwitch>
            )}
          {(allowedServiceFeatures.includes('email_campaigns_active') ||
            (emailMarketingTrialDate &&
              !isEmailMarketingTrialExpired(
                emailMarketingTrialDate,
                Timezone
              ))) && (
            <DiscountsModalSwitch>
              <div className='LeftBlock'>
                <SwitchS2E
                  labelPlacement='start'
                  checked={formik.values?.marketing?.email?.isEnabled}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      dispatch(OPEN_EMAIL_MARKETING_TURN_OFF_MODAL(true));
                    } else {
                      formik.setFieldValue('marketing.email.isEnabled', true);
                    }
                  }}
                  name='marketing.email.isEanbled'
                  isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                />
                <span className='switcher-text'>
                  {formik.values?.marketing?.email?.isEnabled
                    ? 'Active'
                    : 'Disabled'}
                </span>
              </div>
              <div className='RightBlock'>
                <div className='title'>Include in Email Marketing</div>
                <div className='description'>
                  Display this discount in your email marketing campaigns
                </div>
                <ErrorSignWrapper className='ErrorSignWrapper noWrap'>
                  {formik.errors?.marketing?.email?.isEnabled &&
                  formik.touched?.marketing?.email?.isEnabled ? (
                    <ErrorSign>
                      {formik.errors.marketing.email.isEnabled}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </div>
            </DiscountsModalSwitch>
          )}
          {enabledDiscount && (
            <DiscountsModalSwitch>
              <div className='LeftBlock'>
                <SwitchS2E
                  labelPlacement='start'
                  checked={visualChecked}
                  onChange={(e) => {
                    formik.setFieldValue('isPromotional', !e.target.checked);
                  }}
                  name='isPromotional'
                  isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                />
                <span className='switcher-text'>
                  {!formik.values.isPromotional ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className='RightBlock'>
                <div className='title'>Hide from Storefront</div>
                <div className='description'>
                  If on, the discount won’t appear on your storefront
                </div>
              </div>
            </DiscountsModalSwitch>
          )}
          <DiscountsModalSwitch>
            <div className='LeftBlock'>
              <SwitchS2E
                labelPlacement='start'
                checked={enabledCustomAvailability}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setDaysPickerContext([]);
                    formik.setFieldValue('availability.activatesAtLocal', null);
                    formik.setFieldValue(
                      'availability.deactivatesAtLocal',
                      null
                    );
                    setEnabledDate(false);
                    setEnabledAdvancedSettings(false);
                    formik.setFieldValue('availability.weekly', null);
                  }
                  setEnabledCustomAvailability(e.target.checked);
                }}
                name='enabledCustomAvailability'
                isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
              />
              <span className='switcher-text'>
                {enabledCustomAvailability ? 'Active' : 'Disabled'}
              </span>
            </div>
            <div className='RightBlock'>
              <div className='title'>Custom availability</div>
              <div className='description'>
                Set specific date, dates and hour
              </div>
            </div>
          </DiscountsModalSwitch>
          {enabledCustomAvailability && (
            <>
              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledDate(checked);
                        if (!checked) {
                          formik.setFieldValue(
                            'availability.activatesAtLocal',
                            null
                          );
                          formik.setFieldValue(
                            'availability.deactivatesAtLocal',
                            null
                          );
                        }
                      }}
                      checked={enabledDate}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='From'
                />
                <DashboardFieldDatepicker>
                  <DatepickerS2E
                    hideTextFrom
                    disablePast
                    disabled={
                      !enabledDate ||
                      discount.status === DISCOUNT_STATUS.ARCHIVED
                    }
                    value={[
                      formik.values?.availability?.activatesAtLocal
                        ? moment(formik.values.availability.activatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null,
                      formik.values?.availability?.deactivatesAtLocal
                        ? moment(formik.values.availability.deactivatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null
                    ]}
                    onClose={(value) => {
                      if (value && value.length) {
                        formik.setFieldValue(
                          'availability.activatesAtLocal',
                          moment(value[0])
                            .utcOffset(0, true)
                            .startOf('day')
                            .format()
                        );
                        formik.setFieldValue(
                          'availability.deactivatesAtLocal',
                          moment(value[1])
                            .utcOffset(0, true)
                            .endOf('day')
                            .format()
                        );
                      }
                    }}
                    clearSelectedValue
                  />
                </DashboardFieldDatepicker>
                <ErrorSignWrapper
                  className='ErrorSignWrapper'
                  leftPosition='93px'
                  topPosition='-14%'
                >
                  {formik.errors?.availability?.activatesAtLocal &&
                  formik.touched?.availability?.activatesAtLocal ? (
                    <ErrorSign>
                      {formik.errors.availability.activatesAtLocal}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardField>

              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledAdvancedSettings(checked);
                        if (!checked) {
                          formik.setFieldValue('availability.weekly', null);
                          setDaysPickerContext([]);
                        }
                      }}
                      checked={enabledAdvancedSettings}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='Advanced settings'
                />
              </DashboardField>

              {enabledAdvancedSettings && (
                <DashboardField className='paddingR0 paddingL0'>
                  <DaysPickerContainer
                    WorkingDays={convertScheduleFromServiceFormat(
                      formik?.values?.availability?.weekly || []
                    )}
                  />
                </DashboardField>
              )}
            </>
          )}
          <DiscountsModalButton>
            <ButtonPrimary
              type='button'
              className={cn('ButtonDashboard', {
                disabled:
                  buttonStatus || discount.status === DISCOUNT_STATUS.ARCHIVED
              })}
              onClick={(e) => {
                e.stopPropagation();

                if (
                  formik.values?.rate?.type === DISCOUNT_TYPE.FIXED &&
                  formik?.values?.rate?.value &&
                  formik.values?.rate?.value >= formik.values?.subtotal?.min
                ) {
                  formik.setFieldError(
                    'subtotal.min',
                    'Discount cannot be bigger than min order!'
                  );
                  return;
                }

                if (!enabledDiscount) {
                  formik.setFieldValue('promoCode', null);
                }

                formik.setFieldValue(
                  'availability.weekly',
                  enabledAdvancedSettings
                    ? convertScheduleToServiceFormat(daysPickerContext)
                    : null
                );

                formik.handleSubmit(e);
              }}
            >
              <span>Save</span>
            </ButtonPrimary>
          </DiscountsModalButton>
        </DialogBody>
      </DialogContainer>
      {isEmailMarketingTurnOffModalOpen && (
        <EmailMarketingTurnOffModal
          isOpen={isEmailMarketingTurnOffModalOpen}
          setFieldValue={formik.setFieldValue}
        />
      )}
    </Dialog>
  );
};

SettingsModal.propTypes = {
  formik: PropTypes.object.isRequired,
  discount: PropTypes.object.isRequired,
  enabledDiscount: PropTypes.bool.isRequired,
  setEnabledDiscount: PropTypes.func.isRequired,
  enabledDate: PropTypes.bool.isRequired,
  setEnabledDate: PropTypes.func.isRequired,
  buttonStatus: PropTypes.bool.isRequired,
  allowedServiceFeatures: PropTypes.array.isRequired,
  enabledCustomAvailability: PropTypes.bool.isRequired,
  setEnabledCustomAvailability: PropTypes.func.isRequired,
  enabledAdvancedSettings: PropTypes.bool.isRequired,
  setEnabledAdvancedSettings: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onCloseAction: PropTypes.func.isRequired
};

export default SettingsModal;
